import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";

const TRUSTED_KEY = "lif_trusted_device";

/**
 * Login flow stages:
 *   email         → user enters email only. Backend tells us:
 *                     - needs password (returning user)
 *                     - needs activation (never set password → OTP)
 *                     - email not found (show "iscriviti")
 *   password      → classic email+password login
 *   admin-otp     → admin 2FA (code sent to admin email)
 *   activate-otp  → user enters OTP + sets new password
 *   not-found     → email not in waitlist (CTA → signup)
 */

const LoginPage = () => {
  const { refresh } = useAuth();
  const nav = useNavigate();

  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [otpMask, setOtpMask] = useState("");
  const [loading, setLoading] = useState(false);

  const finalize = async (data) => {
    localStorage.setItem("lif_token", data.token);
    if (data.trusted_device_token) {
      localStorage.setItem(TRUSTED_KEY, data.trusted_device_token);
    }
    await refresh();
    toast.success("Accesso effettuato.");
    nav(data.user?.is_admin ? "/admin" : "/area");
  };

  // Stage 1 – user types email, we probe backend
  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const trusted = localStorage.getItem(TRUSTED_KEY);
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        trusted_device_token: trusted || null,
      });
      // Admin went through: not possible without password
      if (data.requires_activation) {
        // Request OTP automatically to speed things up
        try {
          const r = await api.post("/auth/request-activation", {
            email: email.trim().toLowerCase(),
          });
          setOtpMask(r.data.otp_email_masked || data.otp_email_masked || "");
          setStage("activate-otp");
          toast.info(`Codice inviato a ${r.data.otp_email_masked || "la tua email"}`);
        } catch (err2) {
          toast.error(
            err2?.response?.data?.detail || "Invio codice fallito. Riprova."
          );
        }
      } else if (data.token) {
        await finalize(data);
      } else {
        // Backend wants password (admin or activated user)
        setStage("password");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        setStage("not-found");
      } else if (status === 400 && detail === "Password richiesta") {
        // User has password → go to password stage
        setStage("password");
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore di accesso.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Stage 2a – classic email+password
  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const trusted = localStorage.getItem(TRUSTED_KEY);
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
        trusted_device_token: trusted || null,
      });
      if (data.requires_otp) {
        setOtpMask(data.otp_email_masked || "");
        setStage("admin-otp");
        toast.info(`Codice inviato a ${data.otp_email_masked || "email"}`);
      } else {
        await finalize(data);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Credenziali non valide. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Errore di accesso.");
    } finally {
      setLoading(false);
    }
  };

  // Stage 2b – admin 2FA code
  const submitAdminOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
      });
      await finalize(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Codice non valido. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Verifica fallita.");
    } finally {
      setLoading(false);
    }
  };

  // Stage 2c – user activation: OTP + set password
  const submitActivate = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/activate", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        password: newPassword,
      });
      await finalize(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Attivazione fallita. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Errore.");
    } finally {
      setLoading(false);
    }
  };

  const resendActivationCode = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/request-activation", {
        email: email.trim().toLowerCase(),
      });
      setOtpMask(r.data.otp_email_masked || "");
      toast.success(`Nuovo codice inviato a ${r.data.otp_email_masked || "la tua email"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio fallito.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------- UI -----------------------
  const inputCls =
    "w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white placeholder:text-white/40 outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body";

  return (
    <div className="section-dark min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center">
          <Logo size={32} />
          <span className="font-mono font-semibold text-[13px] tracking-[0.4em] text-white">
            L·I·F
          </span>
        </Link>

        {/* ========================= STAGE: EMAIL ========================= */}
        {stage === "email" && (
          <>
            <h1
              className="mt-10 font-display font-bold text-white leading-none text-center"
              style={{ fontSize: "clamp(40px, 6vw, 72px)" }}
            >
              Accedi.
            </h1>
            <p className="mt-4 text-center font-body text-white/60">
              Entra nella tua area personale LIF.
            </p>

            <form onSubmit={submitEmail} className="mt-10 space-y-5">
              <label className="block">
                <span className="eyebrow mb-2 block">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  placeholder="tu@indirizzo.it"
                  autoComplete="email"
                  autoFocus
                  className={inputCls}
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="login-email-submit"
                className="lif-btn w-full py-4"
              >
                {loading ? "Verifica in corso..." : "Continua"}
              </button>
              <p className="text-center font-mono text-[11px] tracking-[0.2em] uppercase text-white/40">
                Prima volta? Riceverai un codice di attivazione via email.
              </p>
            </form>
          </>
        )}

        {/* ========================= STAGE: PASSWORD ========================= */}
        {stage === "password" && (
          <>
            <h1
              className="mt-10 font-display font-bold text-white leading-none text-center"
              style={{ fontSize: "clamp(36px, 5.4vw, 60px)" }}
            >
              Bentornato.
            </h1>
            <p className="mt-4 text-center font-body text-white/60">
              Inserisci la password per <strong className="text-white">{email}</strong>.
            </p>

            <form onSubmit={submitPassword} className="mt-10 space-y-5">
              <label className="block">
                <span className="eyebrow mb-2 block">Password</span>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  autoFocus
                  className={inputCls}
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="login-password-submit"
                className="lif-btn w-full py-4"
              >
                {loading ? "Verifica in corso..." : "Entra"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStage("email");
                  setPassword("");
                }}
                className="w-full font-mono text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-white transition-colors"
              >
                ← Cambia email
              </button>
            </form>
          </>
        )}

        {/* ========================= STAGE: ADMIN OTP ========================= */}
        {stage === "admin-otp" && (
          <>
            <h1
              className="mt-10 font-display font-bold text-white leading-none text-center"
              style={{ fontSize: "clamp(36px, 5.4vw, 60px)" }}
            >
              Codice di accesso.
            </h1>
            <p className="mt-4 text-center font-body text-white/60">
              Ti abbiamo mandato un codice a 6 cifre su{" "}
              <strong className="text-white">{otpMask}</strong>.
            </p>

            <div className="mt-6 border border-[color:var(--lif-accent)]/30 bg-[color:var(--lif-accent)]/5 rounded-md px-4 py-3 text-center">
              <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[color:var(--lif-accent)]">
                ⚠ Non la trovi?
              </p>
              <p className="mt-1 font-body text-xs text-white/70">
                Controlla <strong className="text-white">Spam</strong> o{" "}
                <strong className="text-white">Promozioni</strong>.
              </p>
            </div>

            <form onSubmit={submitAdminOtp} className="mt-6 space-y-5">
              <label className="block">
                <span className="eyebrow mb-2 block">Codice</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  data-testid="login-otp-input"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-4 text-white placeholder:text-white/20 outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-mono text-center text-2xl tracking-[0.5em]"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="login-otp-submit"
                className="lif-btn w-full py-4"
              >
                {loading ? "Verifica..." : "Verifica codice"}
              </button>
              <p className="text-center font-mono text-[11px] tracking-[0.2em] uppercase text-white/40">
                Il codice scade in 10 min · Dispositivo ricordato per 24h
              </p>
              <button
                type="button"
                onClick={() => {
                  setStage("email");
                  setCode("");
                  setPassword("");
                }}
                className="w-full font-mono text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-white transition-colors"
              >
                ← Cambia email
              </button>
            </form>
          </>
        )}

        {/* ========================= STAGE: ACTIVATE (OTP + SET PWD) ========================= */}
        {stage === "activate-otp" && (
          <>
            <h1
              className="mt-10 font-display font-bold text-white leading-none text-center"
              style={{ fontSize: "clamp(32px, 4.6vw, 52px)" }}
            >
              Attiva il tuo accesso.
            </h1>
            <p className="mt-4 text-center font-body text-white/60">
              Ti abbiamo mandato un codice a 6 cifre su{" "}
              <strong className="text-white">{otpMask}</strong>.
              <br />
              Inserisci il codice e scegli una password.
            </p>

            {/* Spam notice */}
            <div
              data-testid="spam-notice"
              className="mt-6 border border-[color:var(--lif-accent)]/30 bg-[color:var(--lif-accent)]/5 rounded-md px-4 py-3 text-center"
            >
              <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[color:var(--lif-accent)]">
                ⚠ Non la trovi?
              </p>
              <p className="mt-1 font-body text-xs text-white/70 leading-relaxed">
                Controlla <strong className="text-white">Spam</strong>,{" "}
                <strong className="text-white">Promozioni</strong> o{" "}
                <strong className="text-white">Aggiornamenti</strong>. La mail
                arriva da <strong className="text-white">LIF</strong> entro 60 secondi.
              </p>
            </div>

            <form onSubmit={submitActivate} className="mt-6 space-y-5">
              <label className="block">
                <span className="eyebrow mb-2 block">Codice ricevuto</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  data-testid="activate-otp-input"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-4 text-white placeholder:text-white/20 outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-mono text-center text-2xl tracking-[0.5em]"
                />
              </label>
              <label className="block">
                <span className="eyebrow mb-2 block">Nuova password (min 8)</span>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="activate-new-password-input"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="eyebrow mb-2 block">Conferma password</span>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="activate-confirm-password-input"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className={inputCls}
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="activate-submit"
                className="lif-btn w-full py-4"
              >
                {loading ? "Attivazione..." : "Attiva account"}
              </button>
              <button
                type="button"
                onClick={resendActivationCode}
                disabled={loading}
                data-testid="resend-code-btn"
                className="w-full py-3 border border-white/20 rounded-md font-mono text-[11px] tracking-[0.24em] uppercase text-white hover:border-[color:var(--lif-accent)] hover:text-[color:var(--lif-accent)] transition-colors disabled:opacity-50"
              >
                ↻ Rinvia codice
              </button>
              <button
                type="button"
                onClick={() => {
                  setStage("email");
                  setCode("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="w-full font-mono text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-white transition-colors"
              >
                ← Cambia email
              </button>
            </form>
          </>
        )}

        {/* ========================= STAGE: NOT FOUND ========================= */}
        {stage === "not-found" && (
          <>
            <h1
              className="mt-10 font-display font-bold text-white leading-none text-center"
              style={{ fontSize: "clamp(32px, 4.6vw, 52px)" }}
            >
              Non sei iscritto.
            </h1>
            <p className="mt-4 text-center font-body text-white/70">
              L'email <strong className="text-white">{email}</strong> non risulta
              nella lista d'attesa LIF. Per accedere all'area personale devi prima
              iscriverti.
            </p>
            <div className="mt-10 space-y-4">
              <Link
                to="/#iscriviti"
                data-testid="goto-signup-link"
                className="lif-btn w-full py-4 text-center block"
              >
                Iscriviti ora →
              </Link>
              <button
                type="button"
                onClick={() => {
                  setStage("email");
                  setEmail("");
                }}
                className="w-full font-mono text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-white transition-colors"
              >
                ← Riprova con un'altra email
              </button>
            </div>
          </>
        )}

        <p className="mt-8 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-white/40">
          <Link to="/" className="hover:text-white transition-colors">
            ← Torna al sito
          </Link>
        </p>
      </div>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
};

export default LoginPage;
